import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Checkbox, Dialog, Typography } from "@mui/material";
import { DataGrid, GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import axios from "axios";
import { io } from "socket.io-client";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { printHtmlTable } from "../../../utils/printTable";
import CommonDropDown from "../../common/dropdown/CommonDropdown";
import { COLORS, STATUS, STATUS_OPTIONS } from "./constants";
import InventoryTabs from "./InventoryTabs";
import ScanHelpTooltip from "./ScanHelpTooltip";
import ActivateGameModal from "./ActivateGameModal";
import BulkConfirmModal from "./BulkConfirmModal";
import PackReturnModal from "./PackReturnModal";

// Readable label for a DB status value
const statusLabel = (status: string): string => {
  switch (status) {
    case STATUS.CONFIRM:
      return "Confirm";
    case STATUS.ACTIVE:
      return "Active";
    case STATUS.SOLD_OUT:
      return "Soldout";
    case STATUS.RETURNED:
      return "Returned";
    default:
      return status;
  }
};

const emptyMark = (value?: string) => (value && value !== "" ? value : "-");

// static, so it lives outside the component rather than being rebuilt each render
const fieldSx = {
  backgroundColor: "#fff",
  border: `1px solid #434343`,
  borderRadius: "2px",
  height: "38px",
  px: "10px",
  fontSize: "17px",
  "& .MuiInputBase-input": { padding: 0 },
} as const;

// A returned pack can be restored for 3 days from its return date (UI gate; the
// backend is the authority). returnedDate is a "YYYY-MM-DD" string.
const withinRestoreWindow = (returnedDate?: string): boolean => {
  if (!returnedDate) return false;
  const diff = Math.floor((Date.now() - new Date(`${returnedDate}T00:00:00`).getTime()) / 86400000);
  return diff >= 0 && diff <= 3;
};

interface ConfirmInventoryProps {
  onGoSales?: () => void;
  onGoSettlement?: () => void;
}

const ConfirmInventory: React.FC<ConfirmInventoryProps> = ({ onGoSales, onGoSettlement }) => {
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const currencySymbol = config?.currencySymbol ?? "$";
  const shopId = JSON.parse(localStorage.getItem("UserData") || "{}")?.uId;
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  // grid data
  const [inventories, setInventories] = React.useState<Array<any>>([]);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [paginationModel, setPaginationModel] = React.useState({ page: 0, pageSize: 10 });
  const [refreshToken, setRefreshToken] = React.useState<number>(0);
  const refresh = () => setRefreshToken(Math.random());

  // single-ticket entry
  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  const [confirming, setConfirming] = React.useState<boolean>(false);

  // filters (inputs vs applied)
  const [statusInput, setStatusInput] = React.useState<string>(STATUS.ALL);
  const [fromDateInput, setFromDateInput] = React.useState<Dayjs | null>(null);
  const [toDateInput, setToDateInput] = React.useState<Dayjs | null>(null);
  const [appliedFilters, setAppliedFilters] = React.useState({ status: STATUS.ALL, fromDate: "", toDate: "" });

  // selection for unconfirm (only CONFIRM rows expose a checkbox)
  const [selectedIds, setSelectedIds] = React.useState<Array<number>>([]);
  const [unconfirming, setUnconfirming] = React.useState<boolean>(false);

  // modals + socket (socket drives the activate / pack-return / restore flows)
  const [activateOpen, setActivateOpen] = React.useState<boolean>(false);
  const [bulkOpen, setBulkOpen] = React.useState<boolean>(false);
  const [packReturnOpen, setPackReturnOpen] = React.useState<boolean>(false);
  const [restoreTarget, setRestoreTarget] = React.useState<any>(null); // returned row pending restore confirm
  const [restoring, setRestoring] = React.useState<boolean>(false);

  // Guards against confirming the same scanned ticket twice (a barcode scanner
  // fires a burst of key events and usually an Enter terminator too).
  const processedScanRef = React.useRef<string>("");
  // the cursor starts in the scan field so a scanner can fire straight into it
  const scanRef = React.useRef<HTMLInputElement | null>(null);
  const [socket, setSocket] = React.useState<any>();

  React.useEffect(() => {
    setLoading(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/fetch`, {
        userId: shopId,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        status: appliedFilters.status,
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
      })
      .then((res: any) => {
        setInventories(res?.data?.inventories ?? []);
        setTotalCount(res?.data?.totalRecords ?? 0);
      })
      .catch(() => showErrorMessage(t("somethingWentWrong", "Something went wrong")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, appliedFilters, refreshToken]);

  React.useEffect(() => {
    const established = io(apiBaseURL, { autoConnect: true, query: { userId: shopId } });
    setSocket(established);
    return () => {
      established && established.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Restore (un-return) acks - the Restore button on a RETURNED row emits over the socket
  React.useEffect(() => {
    if (!socket) return;
    const onRestored = () => {
      setRestoring(false);
      setRestoreTarget(null);
      showSuccessMessage(t("restoreSuccessMsg", "Pack restored successfully"));
      refresh();
    };
    const onRestoreError = (response: any) => {
      setRestoring(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };
    socket.on("restoreInventoryV2Response", onRestored);
    socket.on("restoreInventoryV2Error", onRestoreError);
    return () => {
      socket.off("restoreInventoryV2Response", onRestored);
      socket.off("restoreInventoryV2Error", onRestoreError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Clearing the scan box also has to release the duplicate-scan guard, otherwise
  // re-scanning the ticket that just failed is silently ignored.
  React.useEffect(() => {
    const timer = setTimeout(() => scanRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, []);

  const clearScan = () => {
    setScanCode("");
    processedScanRef.current = "";
  };

  const clearEntry = () => {
    clearScan();
    setGameNumber("");
    setPackNumber("");
  };

  // Confirm a single ticket. Takes explicit game/pack so a scan can submit the
  // freshly parsed values without waiting for the input state to update.
  const submitConfirm = (game: string, pack: string) => {
    if (!/^\d{4}$/.test(game)) {
      clearScan();
      return showErrorMessage(t("gameNumberFourDigitsError", "The game number must be 4 digits."));
    }
    if (!/^\d{7}$/.test(pack)) {
      clearScan();
      return showErrorMessage(t("packNumberSevenDigitsError", "The pack number should be 7 digits."));
    }
    setConfirming(true);
    axios
      .post(`${apiBaseURL}/api/inventory-v2/confirm`, { userId: shopId, gameNumber: game, packNumber: pack })
      .then(() => {
        showSuccessMessage(t("confirmSuccessMsg", "Ticket confirmed successfully"));
        clearEntry();
        refresh();
      })
      .catch((error: any) =>
        showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong")),
      )
      .finally(() => {
        setConfirming(false);
        // the scan box empties whether the confirm succeeded or failed, so the next
        // scan always starts from a clean field (as the bulk popup already did)
        clearScan();
      });
  };

  const handleConfirm = () => submitConfirm(gameNumber, packNumber);

  // Reuse the documented barcode split: game = first 4 chars, pack = next 7.
  // A completed scan fills the fields and confirms the ticket automatically.
  const processScan = (code: string) => {
    const game = code.substring(0, 4);
    const pack = code.substring(4, 11);
    const key = `${game}-${pack}`;
    if (key === processedScanRef.current) return;
    processedScanRef.current = key;
    setGameNumber(game);
    setPackNumber(pack);
    submitConfirm(game, pack);
  };

  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length < 14) processedScanRef.current = "";
    if (value.length >= 11) {
      setGameNumber(value.substring(0, 4));
      setPackNumber(value.substring(4, 11));
    }
    if (value.length >= 14) processScan(value);
  };

  // Enter on the scan field (barcode terminator / manual paste).
  const onScanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = scanCode.trim();
    if (code.length >= 11) processScan(code);
  };

  // Enter on the manual game / pack fields confirms the ticket.
  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!confirming) handleConfirm();
  };

  const applyFilter = () => {
    setSelectedIds([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setAppliedFilters({
      status: statusInput,
      fromDate: fromDateInput ? fromDateInput.format("YYYY-MM-DD") : "",
      toDate: toDateInput ? toDateInput.format("YYYY-MM-DD") : "",
    });
  };

  const toggleSelected = React.useCallback((id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleUnconfirm = async () => {
    if (selectedIds.length === 0) {
      return showErrorMessage(t("unconfirmSelectError", "Select a confirmed ticket to unconfirm"));
    }
    setUnconfirming(true);
    try {
      for (const id of selectedIds) {
        await axios.post(`${apiBaseURL}/api/inventory-v2/unconfirm`, { userId: shopId, id });
      }
      showSuccessMessage(t("unconfirmSuccessMsg", "Ticket(s) unconfirmed successfully"));
      setSelectedIds([]);
      refresh();
    } catch (error: any) {
      showErrorMessage(error?.response?.data?.message ?? t("somethingWentWrong", "Something went wrong"));
    } finally {
      setUnconfirming(false);
    }
  };

  const handleRestore = () => {
    if (!restoreTarget) return;
    if (!socket) return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    setRestoring(true);
    socket.emit("restoreInventoryV2", { shopId, id: restoreTarget.id });
  };

  // [19] only the table is printed - window.print() put the sidebar, the filter bar
  // and every button on the page into the printout
  const printInventories = () => {
    const ok = printHtmlTable({
      title: t("inventoryPrintTitle", "Game Confirm/Inventory"),
      subtitle: `${t("statusHeaderText", "Status")}: ${statusLabel(appliedFilters.status)}${
        appliedFilters.fromDate ? ` | ${appliedFilters.fromDate} - ${appliedFilters.toDate}` : ""
      }`,
      head: [
        t("lotteryNameHeaderText", "Lottery Name"),
        t("gameNumberHeaderText", "Game #"),
        t("packNumberHeaderText", "Pack #"),
        t("gameAmountHeaderText", "Game Amount"),
        t("confirmDateHeaderText", "Confirm Date"),
        t("activateDateHeaderText", "Activate Date"),
        t("soldoutDateHeaderText", "Soldout Date"),
        t("statusHeaderText", "Status"),
      ],
      rows: inventories.map((r: any) => [
        r.lotteryName,
        r.gameNumber,
        r.packNumber,
        `${currencySymbol}${r.gameAmount}`,
        emptyMark(r.confirmDate),
        emptyMark(r.activeDate),
        emptyMark(r.soldOutDate),
        statusLabel(r.status),
      ]),
    });
    if (!ok) showErrorMessage(t("popupBlocked", "Please allow pop-ups to print"));
  };

  const columns: GridColDef[] = React.useMemo(() => [
    { field: "lotteryName", headerName: t("lotteryNameHeaderText", "Lottery Name"), flex: 1.4, align: "center", headerAlign: "center" },
    { field: "gameNumber", headerName: t("gameNumberHeaderText", "Game #"), flex: 1, align: "center", headerAlign: "center" },
    { field: "packNumber", headerName: t("packNumberHeaderText", "Pack #"), flex: 1, align: "center", headerAlign: "center" },
    {
      field: "gameAmount",
      headerName: t("gameAmountHeaderText", "Game Amount"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => <span>{`${currencySymbol}${params.row.gameAmount}`}</span>,
    },
    { field: "confirmDate", headerName: t("confirmDateHeaderText", "Confirm Date"), flex: 1, align: "center", headerAlign: "center" },
    {
      field: "activeDate",
      headerName: t("activateDateHeaderText", "Activate Date"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => <span>{emptyMark(params.row.activeDate)}</span>,
    },
    {
      field: "soldOutDate",
      headerName: t("soldoutDateHeaderText", "Soldout Date"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => <span>{emptyMark(params.row.soldOutDate)}</span>,
    },
    {
      field: "status",
      headerName: t("statusHeaderText", "Status"),
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => {
        // A returned pack shows "Returned" (red) with its return date beneath
        if (params.row.status === STATUS.RETURNED) {
          const returnedDate = params.row.metadata?.return?.returnedDate;
          return (
            <Box sx={{ lineHeight: 1.25 }}>
              <Typography component='div' sx={{ color: COLORS.returnedText, fontWeight: 600, fontSize: "16px" }}>
                {t("returnedStatusLabel", "Returned")}
              </Typography>
              {returnedDate ? (
                <Typography component='div' sx={{ color: COLORS.returnedText, fontSize: "14px" }}>
                  {returnedDate}
                </Typography>
              ) : null}
            </Box>
          );
        }
        return <span>{statusLabel(params.row.status)}</span>;
      },
    },
    {
      field: "action",
      headerName: t("actionHeaderText", "Action"),
      flex: 0.8,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridCellParams) => {
        // A returned pack can be restored while inside its 3-day window
        if (params.row.status === STATUS.RETURNED) {
          return withinRestoreWindow(params.row.metadata?.return?.returnedDate) ? (
            <Button
              onClick={() => setRestoreTarget(params.row)}
              disableElevation
              sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "6px", height: "30px", fontSize: "14px", fontWeight: 600, px: 1.5, "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
            >
              {t("restoreButton", "Restore")}
            </Button>
          ) : null;
        }
        // Otherwise the checkbox is only available while the ticket is still CONFIRM (for Unconfirm)
        return params.row.status === STATUS.CONFIRM ? (
          <Checkbox
            size='small'
            checked={selectedIds.includes(params.row.id)}
            onChange={() => toggleSelected(params.row.id)}
          />
        ) : null;
      },
    },
  ], [t, currencySymbol, selectedIds, toggleSelected]);

  return (
    <Box p={2}>
      {/* Header: title + tab navigation */}
      <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap='12px' mb={2}>
        <Typography sx={{ fontSize: "30px", fontWeight: 700, color: "#000" }}>
          {t("confirmInventoryTitle", "Game Confirm/Inventory")}
        </Typography>
        <InventoryTabs active='confirm' onActivateClick={() => setActivateOpen(true)} onSalesClick={onGoSales} onSettlementClick={onGoSettlement} />
      </Box>

      {/* Entry bar: Bulk Confirm (left, vertically centered) + compact scan/manual entry panel pushed to the right */}
      <Box display='flex' alignItems='center' justifyContent='space-between' gap='16px' flexWrap='wrap' mb={2}>
        <Button
          onClick={() => setBulkOpen(true)}
          disableElevation
          sx={{
            backgroundColor: COLORS.orange,
            color: "#fff",
            textTransform: "none",
            borderRadius: "10px",
            height: "38px",
            px: 2,
            fontSize: "16px",
            fontWeight: 600,
            "&:hover": { backgroundColor: COLORS.orange, opacity: 0.9 },
          }}
        >
          {t("bulkConfirmButton", "Bulk Confirm")}
        </Button>

        {/* Compact panel, kept content-width and right-aligned via the row's space-between */}
        <Box
          display='flex'
          alignItems='flex-end'
          gap='18px'
          flexWrap='wrap'
          sx={{ backgroundColor: COLORS.panelBg, border: "0.25px solid #434343", p: 2, borderRadius: "2px" }}
        >
          {/* Scan Code + help */}
          <Box>
            <Typography sx={{ fontSize: "17px", color: "#434343", mb: "4px" }}>{t("scanCodeLabel", "Scan Code")}</Typography>
            <Box display='flex' alignItems='center' gap='8px'>
              <Box
                component='input'
                ref={scanRef}
                autoFocus
                value={scanCode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)}
                onKeyDown={onScanKeyDown}
                placeholder={t("scanCodePlaceholder", "Scan / enter barcode")}
                sx={{ ...fieldSx, width: "240px", outline: "none" }}
              />
              <ScanHelpTooltip />
            </Box>
          </Box>

          {/* OR separator */}
          <Typography sx={{ fontSize: "17px", color: "#434343", pb: "8px" }}>{t("or", "OR")}</Typography>

          {/* Manual entry: Game # */}
          <Box>
            <Typography sx={{ fontSize: "17px", color: "#434343", mb: "4px" }}>{t("gameNumberLabel", "Game Number")}</Typography>
            <Box
              component='input'
              value={gameNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)}
              onKeyDown={onEntryKeyDown}
              sx={{ ...fieldSx, width: "150px", outline: "none" }}
            />
          </Box>

          {/* Manual entry: Pack # */}
          <Box>
            <Typography sx={{ fontSize: "17px", color: "#434343", mb: "4px" }}>{t("packNumberLabel", "Pack Number")}</Typography>
            <Box
              component='input'
              value={packNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)}
              onKeyDown={onEntryKeyDown}
              sx={{ ...fieldSx, width: "170px", outline: "none" }}
            />
          </Box>

          <Button
            onClick={handleConfirm}
            disabled={confirming}
            disableElevation
            sx={{
              backgroundColor: COLORS.blue,
              color: "#fff",
              textTransform: "none",
              borderRadius: "10px",
              height: "38px",
              px: 3,
              fontSize: "16px",
              fontWeight: 600,
              "&:hover": { backgroundColor: COLORS.blue, opacity: 0.9 },
            }}
          >
            {t("enterButton", "Enter")}
          </Button>
        </Box>
      </Box>

      {/* Filter bar */}
      <Box
        display='flex'
        alignItems='center'
        gap='14px'
        flexWrap='wrap'
        sx={{ backgroundColor: COLORS.panelBg, border: `0.25px solid #434343`, p: 1.5, borderRadius: "2px", mb: 2 }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "17px" }}>{t("statusFilterLabel", "Status :")}</Typography>
        <CommonDropDown
          value={statusInput}
          name='statusFilter'
          onChange={(e) => setStatusInput(e.target.value)}
          options={STATUS_OPTIONS}
          sx={{ minWidth: "170px", backgroundColor: "#fff" }}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={fromDateInput}
            onChange={(value) => setFromDateInput(value)}
            format='MM/DD/YYYY'
            slotProps={{ textField: { size: "small", sx: { backgroundColor: "#fff", width: "170px" } } }}
          />
          <Typography sx={{ fontSize: "17px" }}>{t("dateRangeTo", "to")}</Typography>
          <DatePicker
            value={toDateInput}
            onChange={(value) => setToDateInput(value)}
            format='MM/DD/YYYY'
            minDate={fromDateInput ?? undefined}
            slotProps={{ textField: { size: "small", sx: { backgroundColor: "#fff", width: "170px" } } }}
          />
        </LocalizationProvider>

        <Button
          onClick={applyFilter}
          disableElevation
          sx={{ backgroundColor: COLORS.grey, color: "#fff", textTransform: "none", borderRadius: "10px", height: "38px", fontSize: "16px", fontWeight: 600, px: 2, "&:hover": { backgroundColor: COLORS.grey, opacity: 0.9 } }}
        >
          {t("filterButton", "Filter")}
        </Button>
        <Button
          onClick={printInventories}
          disableElevation
          sx={{ backgroundColor: COLORS.green, color: "#fff", textTransform: "none", borderRadius: "10px", height: "38px", fontSize: "16px", fontWeight: 600, px: 2, "&:hover": { backgroundColor: COLORS.green, opacity: 0.9 } }}
        >
          {t("printButton", "Print")}
        </Button>
        <Button
          onClick={() => setPackReturnOpen(true)}
          disableElevation
          sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "10px", height: "38px", fontSize: "16px", fontWeight: 600, px: 2, "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
        >
          {t("packReturnButton", "Pack Return")}
        </Button>
        <Button
          onClick={handleUnconfirm}
          disabled={unconfirming}
          disableElevation
          sx={{ backgroundColor: COLORS.red, color: "#fff", textTransform: "none", borderRadius: "10px", height: "38px", fontSize: "16px", fontWeight: 600, px: 2, "&:hover": { backgroundColor: COLORS.red, opacity: 0.9 } }}
        >
          {t("unconfirmButton", "UNCONFIRM")}
        </Button>
      </Box>

      {/* Table */}
      <Box sx={{ width: "100%", height: "560px" }}>
        <DataGrid
          rows={inventories}
          loading={loading}
          columns={columns}
          rowCount={totalCount}
          pageSizeOptions={[10, 20, 50, 100]}
          pagination
          paginationMode='server'
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowId={(row) => row.id}
          disableColumnSelector
          disableRowSelectionOnClick
          getRowClassName={(params) =>
            (params.indexRelativeToCurrentPage % 2 === 0) ? "row-dark" : "row-light"
          }
          sx={{
            border: `1px solid ${COLORS.border}`,
            fontSize: "16px",
            "& .MuiDataGrid-columnHeaders": { backgroundColor: "#fff" },
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, fontSize: "17px" },
            "& .MuiDataGrid-cell": { fontSize: "16px" },
            "& .row-dark": { backgroundColor: COLORS.rowDark },
            "& .row-light": { backgroundColor: COLORS.rowLight },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
            // Reserve the vertical scrollbar's gutter at all times. Without this, the
            // scrollbar appearing narrows the scroller, which re-flows the flex columns,
            // which can push the content just wide enough to need a horizontal scrollbar,
            // which steals height and removes the vertical one again - a loop that makes
            // both scrollbars flicker on their own. Reserving the gutter breaks it.
            "& .MuiDataGrid-virtualScroller": { scrollbarGutter: "stable" },
          }}
        />
      </Box>

      <ActivateGameModal
        open={activateOpen}
        onClose={() => setActivateOpen(false)}
        socket={socket}
        apiBaseURL={apiBaseURL}
        shopId={shopId}
        onActivated={refresh}
      />

      <BulkConfirmModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        apiBaseURL={apiBaseURL}
        shopId={shopId}
        onConfirmed={refresh}
      />

      <PackReturnModal
        open={packReturnOpen}
        onClose={() => setPackReturnOpen(false)}
        socket={socket}
        apiBaseURL={apiBaseURL}
        shopId={shopId}
        onReturned={refresh}
      />

      {/* Restore (un-return) confirmation */}
      <Dialog open={!!restoreTarget} onClose={() => setRestoreTarget(null)} PaperProps={{ sx: { borderRadius: "10px", p: 1 } }}>
        <Box sx={{ p: 3, maxWidth: "420px" }}>
          <Typography sx={{ fontSize: "18px", fontWeight: 700, mb: 1 }}>{t("restoreConfirmTitle", "Restore this pack?")}</Typography>
          <Typography sx={{ fontSize: "15px", color: "#434343", mb: 2 }}>
            {t("restoreConfirmBody", "This will restore the pack to its previous status. If it was active, it will be placed back on its slot.")}
          </Typography>
          <Box display='flex' justifyContent='flex-end' gap='12px'>
            <Button onClick={() => setRestoreTarget(null)} disableElevation sx={{ backgroundColor: COLORS.grey, color: "#fff", textTransform: "none", borderRadius: "6px", px: 2.5, fontSize: "15px", fontWeight: 600, "&:hover": { backgroundColor: COLORS.grey, opacity: 0.9 } }}>
              {t("cancel", "Cancel")}
            </Button>
            <Button onClick={handleRestore} disabled={restoring} disableElevation sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "6px", px: 2.5, fontSize: "15px", fontWeight: 600, "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}>
              {t("restoreButton", "Restore")}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ConfirmInventory;
